class CommonUtils {
    static randomBase62(length: number): string {
        const chars =
            'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        return Array.from(array, (n) => chars[n % chars.length]).join('');
    }

    static async generateKey(): Promise<CryptoKey> {
        return window.crypto.subtle.generateKey(
            {
                name: 'AES-GCM',
                length: 256,
            },
            true,
            ['encrypt', 'decrypt'],
        );
    }

    static async exportKey(key: CryptoKey): Promise<string> {
        const exported = await window.crypto.subtle.exportKey('raw', key);
        return btoa(
            String.fromCharCode(...Array.from(new Uint8Array(exported))),
        );
    }

    static async importKey(keyStr: string): Promise<CryptoKey> {
        const keyBytes = Uint8Array.from(atob(keyStr), (c) => c.charCodeAt(0));
        return window.crypto.subtle.importKey(
            'raw',
            keyBytes,
            'AES-GCM',
            true,
            ['encrypt', 'decrypt'],
        );
    }

    static async encryptChunk(
        key: CryptoKey,
        chunk: ArrayBuffer,
    ): Promise<ArrayBuffer> {
        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const encrypted = await window.crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv,
            },
            key,
            chunk,
        );

        const result = new Uint8Array(iv.length + encrypted.byteLength);
        result.set(iv);
        result.set(new Uint8Array(encrypted), iv.length);
        return result.buffer;
    }

    static async decryptChunk(
        key: CryptoKey,
        chunk: ArrayBuffer,
    ): Promise<ArrayBuffer> {
        const iv = chunk.slice(0, 12);
        const data = chunk.slice(12);

        return window.crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: new Uint8Array(iv),
            },
            key,
            data,
        );
    }

    static async encryptString(key: CryptoKey, str: string): Promise<string> {
        const encoded = new TextEncoder().encode(str);
        const encrypted = await this.encryptChunk(key, encoded.buffer);
        return btoa(
            String.fromCharCode(...Array.from(new Uint8Array(encrypted))),
        );
    }

    static async decryptString(key: CryptoKey, str: string): Promise<string> {
        const encrypted = Uint8Array.from(atob(str), (c) =>
            c.charCodeAt(0),
        ).buffer;
        const decrypted = await this.decryptChunk(key, encrypted);
        return new TextDecoder().decode(decrypted);
    }
    static formatSpeed(bytes: number, timeInMs: number): string {
        if (timeInMs <= 0) return '0 B/s';
        const speed = (bytes / timeInMs) * 1000;

        // Inline filesize-like logic for speed formatting if needed,
        // but here we can just use the provided filesize function if we want to follow the same style.
        // However, filesize returns "10 MB", we want "10 MB/s".

        // Since filesize is external and not available here easily without imports (which CommonUtils doesn't have),
        // I'll implement a simple one.
        const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
        let unitIndex = 0;
        let scaledSpeed = speed;
        while (scaledSpeed >= 1024 && unitIndex < units.length - 1) {
            scaledSpeed /= 1024;
            unitIndex++;
        }
        return `${scaledSpeed.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    }

    static isMobileDevice(): boolean {
        if (typeof window === 'undefined') return false;

        const userAgent =
            window.navigator.userAgent ||
            window.navigator.vendor ||
            (window as any).opera;

        // Check for iOS
        const isIOS =
            /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

        // Check for Android
        const isAndroid = /android/i.test(userAgent);

        return isIOS || isAndroid;
    }
}

export default CommonUtils;
